import * as React from 'react'
import { Component } from 'react'
import { v4 as uuid } from 'uuid'
import {
  Alert,
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  Col,
  Row,
  Table,
} from 'reactstrap'
import * as Redux from 'redux'
import { RasaContext } from 'context'
import * as GenericRedux from 'generic/genericRedux'
import { injectReducer } from 'store/index'
import { Loading } from 'components/loading'
import * as Flash from 'components/flash'

import * as Constants from './constants'
import {
  fetchAdminSessions,
  fetchSessionHistory,
  renderSystemPrompt,
  streamReplayTurn,
} from './data'
import {
  AdminSession,
  ReplayTurn,
  SessionMessage,
  ViewMode,
} from './types'

import './_styles.scss'

type ChatReplayProps = Record<string, never>

interface ChatReplayState {
  view: ViewMode
  // List view
  communityId: string
  isLoadingList: boolean
  listError: string
  sessions: AdminSession[]
  // Detail view
  selectedSession: AdminSession | null
  isLoadingDetail: boolean
  detailError: string
  messages: SessionMessage[]
  chatType: string | null
  originalSystemPrompt: string
  editedSystemPrompt: string
  isLoadingSystemPrompt: boolean
  // Replay state
  isReplaying: boolean
  replayCancelled: boolean
  turns: ReplayTurn[]
}

class ChatReplayComponent extends Component<ChatReplayProps, ChatReplayState> {
  public static contextType = RasaContext

  constructor(props: ChatReplayProps) {
    super(props)
    this.state = {
      view: 'list',
      communityId: '',
      isLoadingList: false,
      listError: '',
      sessions: [],
      selectedSession: null,
      isLoadingDetail: false,
      detailError: '',
      messages: [],
      chatType: null,
      originalSystemPrompt: '',
      editedSystemPrompt: '',
      isLoadingSystemPrompt: false,
      isReplaying: false,
      replayCancelled: false,
      turns: [],
    }
  }

  public componentDidMount() {
    this.context.user.init().then(({ activeCommunity }: any) => {
      this.setState({ communityId: activeCommunity.communityId }, () => {
        this.loadSessions()
      })
    })
  }

  public render() {
    return (
      <div className="chat-replay-wrapper">
        <div className="chat-replay-header">
          <h4>Chat Session Replay</h4>
          <p className="text-muted">
            Browse past AI chat sessions and re-run them with a modified system prompt.
            Replays use <code>dryRun</code> mode and never write to <code>ai_chat_history</code>.
          </p>
        </div>
        {this.state.view === 'list' ? this.renderListView() : this.renderDetailView()}
      </div>
    )
  }

  private renderListView() {
    return (
      <div className="chat-replay-list">
        <Card className="mb-3">
          <CardBody>
            <Row>
              <Col md="12" className="d-flex justify-content-end">
                <Button
                  color="secondary"
                  size="sm"
                  onClick={this.loadSessions}
                  disabled={this.state.isLoadingList}
                >
                  {this.state.isLoadingList ? 'Loading…' : 'Refresh'}
                </Button>
              </Col>
            </Row>
          </CardBody>
        </Card>

        {this.state.listError && <Alert color="danger">{this.state.listError}</Alert>}

        {this.state.isLoadingList ? (
          <Loading size="32" />
        ) : (
          <Card>
            <CardHeader>
              <strong>Sessions</strong>{' '}
              <Badge color="info">{this.state.sessions.length}</Badge>
            </CardHeader>
            <CardBody>
              {this.state.sessions.length === 0 ? (
                <div className="text-muted">No sessions found.</div>
              ) : (
                <Table responsive striped size="sm">
                  <thead>
                    <tr>
                      <th>Title</th>
                      <th>User</th>
                      <th>Messages</th>
                      <th>Created</th>
                      <th />
                    </tr>
                  </thead>
                  <tbody>
                    {this.state.sessions.map((s) => (
                      <tr key={s.id}>
                        <td>{s.title || <em className="text-muted">(no title)</em>}</td>
                        <td>
                          <div>{s.user_email}</div>
                          <small className="text-muted">{s.user_guid}</small>
                        </td>
                        <td>{s.message_count}</td>
                        <td>{new Date(s.created).toLocaleString()}</td>
                        <td>
                          <Button
                            color="secondary"
                            size="sm"
                            onClick={() => this.openSession(s)}
                          >
                            Open
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              )}
            </CardBody>
          </Card>
        )}
      </div>
    )
  }

  private renderDetailView() {
    const { selectedSession, messages, chatType, turns } = this.state
    if (!selectedSession) return null

    return (
      <div className="chat-replay-detail">
        <div className="mb-3">
          <Button color="link" onClick={this.backToList}>
            ← Back to sessions
          </Button>
        </div>

        <Card className="mb-3">
          <CardBody>
            <h5>{selectedSession.title || '(untitled session)'}</h5>
            <div className="text-muted">
              {selectedSession.user_email} · {selectedSession.session_id}
              {chatType && (
                <>
                  {' · '}
                  <Badge color="secondary">{chatType}</Badge>
                </>
              )}
            </div>
          </CardBody>
        </Card>

        {this.state.detailError && <Alert color="danger">{this.state.detailError}</Alert>}

        {this.state.isLoadingDetail ? (
          <Loading size="32" />
        ) : (
          <>
            <Card className="mb-3">
              <CardHeader>
                <strong>System prompt</strong>
                {this.state.isLoadingSystemPrompt && (
                  <span className="text-muted ml-2">loading…</span>
                )}
                <Button
                  color="link"
                  size="sm"
                  className="float-right"
                  onClick={this.resetSystemPrompt}
                  disabled={
                    !this.state.originalSystemPrompt ||
                    this.state.editedSystemPrompt === this.state.originalSystemPrompt
                  }
                >
                  Reset to original
                </Button>
              </CardHeader>
              <CardBody>
                <textarea
                  className="form-control system-prompt-editor"
                  rows={14}
                  value={this.state.editedSystemPrompt}
                  onChange={(e) =>
                    this.setState({ editedSystemPrompt: e.target.value })
                  }
                  placeholder="System prompt will be loaded from chat type."
                  disabled={this.state.isLoadingSystemPrompt}
                />
                <small className="text-muted">
                  {this.state.editedSystemPrompt.length} chars
                </small>
              </CardBody>
            </Card>

            <Card className="mb-3">
              <CardHeader>
                <strong>Replay</strong>{' '}
                <small className="text-muted">
                  {this.userTurnCount()} user turn(s)
                </small>
                <div className="float-right">
                  {this.state.isReplaying ? (
                    <Button color="warning" size="sm" onClick={this.cancelReplay}>
                      Stop after current turn
                    </Button>
                  ) : (
                    <Button
                      color="primary"
                      size="sm"
                      onClick={this.startReplay}
                      disabled={
                        !this.state.editedSystemPrompt ||
                        !this.state.chatType ||
                        this.userTurnCount() === 0
                      }
                    >
                      Replay all turns
                    </Button>
                  )}
                </div>
              </CardHeader>
              <CardBody>
                {turns.length === 0 ? (
                  <div className="text-muted">
                    Click <em>Replay all turns</em> to re-run the conversation.
                  </div>
                ) : (
                  turns.map((turn) => (
                    <Row key={turn.index} className="replay-turn mb-3">
                      <Col md="12">
                        <div className="user-msg">
                          <Badge color="primary">user</Badge> {turn.originalUser}
                        </div>
                      </Col>
                      <Col md="6">
                        <div className="assistant-msg original">
                          <Badge color="secondary">original</Badge>
                          <pre>{turn.originalAssistant || '(no recorded response)'}</pre>
                        </div>
                      </Col>
                      <Col md="6">
                        <div className={`assistant-msg new status-${turn.status}`}>
                          <div className="d-flex justify-content-between align-items-center">
                            <Badge color={this.statusColor(turn.status)}>
                              {turn.status}
                            </Badge>
                            {turn.updatedHtml && (
                              <div className="btn-group btn-group-sm">
                                <Button
                                  color={(turn.view || 'text') === 'text' ? 'primary' : 'secondary'}
                                  size="sm"
                                  onClick={() => this.updateTurn(turn.index, { view: 'text' })}
                                >
                                  Text
                                </Button>
                                <Button
                                  color={turn.view === 'preview' ? 'primary' : 'secondary'}
                                  size="sm"
                                  onClick={() => this.updateTurn(turn.index, { view: 'preview' })}
                                >
                                  Preview
                                </Button>
                                <Button
                                  color="secondary"
                                  size="sm"
                                  onClick={() => this.openHtmlInNewTab(turn.updatedHtml)}
                                  title="Open in new tab"
                                >
                                  ↗
                                </Button>
                              </div>
                            )}
                          </div>
                          {turn.error ? (
                            <Alert color="danger" className="mt-1 mb-0 py-1">
                              {turn.error}
                            </Alert>
                          ) : turn.view === 'preview' && turn.updatedHtml ? (
                            <iframe
                              className="html-preview"
                              title={`replay-preview-${turn.index}`}
                              srcDoc={turn.updatedHtml}
                              sandbox=""
                            />
                          ) : (
                            <pre>{turn.newAssistant || (turn.status === 'pending' ? '(waiting)' : '…')}</pre>
                          )}
                          {turn.modifications && (
                            <small className="text-muted d-block mt-1">
                              modifications: applied {turn.modifications.applied || 0},
                              failed {turn.modifications.failed || 0}
                            </small>
                          )}
                        </div>
                      </Col>
                    </Row>
                  ))
                )}
              </CardBody>
            </Card>

            <Card>
              <CardHeader>
                <strong>Original transcript</strong>{' '}
                <Badge color="info">{messages.length}</Badge>
              </CardHeader>
              <CardBody>
                {messages.map((m) => (
                  <div key={m.id} className={`transcript-row role-${m.role}`}>
                    <Badge color={m.role === 'user' ? 'primary' : 'secondary'}>
                      {m.role}
                    </Badge>
                    <pre>{m.prompt}</pre>
                  </div>
                ))}
              </CardBody>
            </Card>
          </>
        )}
      </div>
    )
  }

  private statusColor(s: ReplayTurn['status']): string {
    switch (s) {
      case 'done': return 'success'
      case 'running': return 'info'
      case 'error': return 'danger'
      case 'cancelled': return 'warning'
      default: return 'secondary'
    }
  }

  private userTurnCount = (): number => {
    return this.state.messages.filter((m) => m.role === 'user').length
  }

  private loadSessions = () => {
    const { communityId } = this.state
    if (!communityId) return
    this.setState({ isLoadingList: true, listError: '' })
    fetchAdminSessions(communityId, {
      limit: Constants.DEFAULT_PAGE_SIZE,
    })
      .then((sessions) => {
        this.setState({ isLoadingList: false, sessions: sessions || [] })
      })
      .catch((err) => {
        const msg = err && err.message ? err.message : 'Failed to load sessions'
        this.setState({ isLoadingList: false, listError: msg })
      })
  }

  private openSession = (session: AdminSession) => {
    this.setState({
      view: 'detail',
      selectedSession: session,
      isLoadingDetail: true,
      detailError: '',
      messages: [],
      chatType: null,
      originalSystemPrompt: '',
      editedSystemPrompt: '',
      isLoadingSystemPrompt: false,
      isReplaying: false,
      replayCancelled: false,
      turns: [],
    })

    fetchSessionHistory(this.state.communityId, session.session_id)
      .then((response) => {
        this.setState(
          {
            isLoadingDetail: false,
            messages: response.messages || [],
            chatType: response.chatType,
          },
          () => this.loadSystemPromptForSession(),
        )
      })
      .catch((err) => {
        const msg = err && err.message ? err.message : 'Failed to load session history'
        this.setState({ isLoadingDetail: false, detailError: msg })
      })
  }

  private loadSystemPromptForSession = () => {
    const { chatType, communityId, selectedSession, messages } = this.state
    if (!chatType || !selectedSession) {
      return
    }
    const sampleUser = messages.find((m) => m.role === 'user')
    this.setState({ isLoadingSystemPrompt: true })
    renderSystemPrompt(communityId, {
      chatType,
      sessionId: selectedSession.session_id,
      sampleUserMessage: sampleUser ? sampleUser.prompt : '',
      contextOverrides: this.extractOriginalContext(),
    })
      .then((response) => {
        this.setState({
          isLoadingSystemPrompt: false,
          originalSystemPrompt: response.systemPrompt,
          editedSystemPrompt: response.systemPrompt,
        })
      })
      .catch((err) => {
        const msg = err && err.message ? err.message : 'Failed to render system prompt'
        this.setState({
          isLoadingSystemPrompt: false,
          detailError: msg,
        })
      })
  }

  private resetSystemPrompt = () => {
    this.setState({ editedSystemPrompt: this.state.originalSystemPrompt })
  }

  private backToList = () => {
    this.setState({
      view: 'list',
      selectedSession: null,
      messages: [],
      turns: [],
    })
  }

  private cancelReplay = () => {
    this.setState({ replayCancelled: true })
    this.context.store.dispatch(
      Flash.showFlashMessage('Replay will stop after the current turn finishes.'),
    )
  }

  private extractOriginalContext = (): Record<string, any> => {
    // Recover chat-type-specific context from the historical session's
    // ai_chat_history.options metadata so the AI can re-access the same
    // template/account/etc. during replay. Maps stored snake_case keys back
    // to the camelCase req.body fields each build*Context reads.
    const merged: Record<string, any> = {}
    for (const m of this.state.messages) {
      const opts = m.options_parsed
      if (!opts) continue
      Object.keys(opts).forEach((k) => {
        if (merged[k] !== undefined) return
        if (k === 'role' || k === 'chat_type' || k === 'tool_only') return
        merged[k] = opts[k]
      })
    }

    const ctx: Record<string, any> = {}
    if (merged.template_name) ctx.templateName = merged.template_name
    if (merged.file_id) ctx.fileId = merged.file_id
    if (merged.topics) ctx.topics = merged.topics
    if (merged.account_id) ctx.accountId = merged.account_id
    if (merged.summary) ctx.summary = merged.summary
    if (merged.available_indexes) ctx.availableIndexes = merged.available_indexes
    if (merged.default_date_range) ctx.defaultDateRange = merged.default_date_range
    return ctx
  }

  private startReplay = async () => {
    const {
      communityId,
      messages,
      chatType,
      editedSystemPrompt,
    } = this.state

    if (!chatType) return

    const userTurns = messages
      .filter((m) => m.role === 'user' && m.prompt && m.prompt.trim().length > 0)
      .map((m) => m.prompt)

    if (userTurns.length === 0) return

    const extraContext = this.extractOriginalContext()

    // Pair each user turn with the next assistant message in chronological order
    // for side-by-side comparison. ai_chat_history rows are already sorted by `created`.
    const originalAssistants: string[] = []
    let userIdx = 0
    for (let i = 0; i < messages.length && userIdx < userTurns.length; i++) {
      if (messages[i].role !== 'user') continue
      // find first assistant message after index i
      let assistant = ''
      for (let j = i + 1; j < messages.length; j++) {
        if (messages[j].role === 'assistant') {
          assistant = messages[j].prompt
          break
        }
        if (messages[j].role === 'user') break
      }
      originalAssistants.push(assistant)
      userIdx++
    }

    const initialTurns: ReplayTurn[] = userTurns.map((u, i) => ({
      index: i,
      originalUser: u,
      originalAssistant: originalAssistants[i] || '',
      newAssistant: '',
      status: 'pending',
    }))

    this.setState({
      isReplaying: true,
      replayCancelled: false,
      turns: initialTurns,
    })

    // Reuse the original session_id so MCP template tools resolve to the
    // existing S3 path (mcp-templates/sessions/<sessionId>/files/<fileId>).
    // dryRun=true on the chat handler skips persistence, so this won't write
    // new rows to ai_chat_history.
    const replaySessionId = this.state.selectedSession
      ? this.state.selectedSession.session_id
      : uuid()
    const newHistory: { role: string; content: string }[] = []

    for (let i = 0; i < userTurns.length; i++) {
      if (this.state.replayCancelled) {
        this.markRemainingCancelled(i)
        break
      }

      this.updateTurn(i, { status: 'running', newAssistant: '' })

      // eslint-disable-next-line no-await-in-loop
      const turnResult = await new Promise<{
        text: string
        error?: string
        updatedHtml?: string
        fileId?: string
        modifications?: any
      }>((resolve) => {
        let accumulated = ''
        let capturedHtml: string | undefined
        let capturedFileId: string | undefined
        let capturedModifications: any
        streamReplayTurn(
          communityId,
          {
            message: userTurns[i],
            sessionId: replaySessionId,
            chatType,
            systemPromptOverride: editedSystemPrompt,
            conversationHistoryOverride: [...newHistory],
            extraContext,
          },
          (chunk) => {
            if (chunk === null) {
              resolve({
                text: accumulated,
                updatedHtml: capturedHtml,
                fileId: capturedFileId,
                modifications: capturedModifications,
              })
              return
            }
            if (chunk.error) {
              resolve({ text: accumulated, error: chunk.error })
              return
            }
            if (typeof chunk.message === 'string') {
              accumulated += chunk.message
              this.updateTurn(i, { newAssistant: accumulated })
            }
            if (typeof chunk.updatedHtml === 'string' && chunk.updatedHtml.length > 0) {
              capturedHtml = chunk.updatedHtml
              // Default to preview view the first time HTML arrives so the
              // user sees the rendered email immediately.
              this.updateTurn(i, { updatedHtml: chunk.updatedHtml, view: 'preview' })
            }
            if (typeof chunk.fileId === 'string') {
              capturedFileId = chunk.fileId
              this.updateTurn(i, { fileId: chunk.fileId })
            }
            if (chunk.modifications) {
              capturedModifications = chunk.modifications
              this.updateTurn(i, { modifications: chunk.modifications })
            }
          },
        )
      })

      if (turnResult.error) {
        this.updateTurn(i, {
          status: 'error',
          error: turnResult.error,
          newAssistant: turnResult.text,
        })
        // Do not feed an errored turn back into history — break the chain.
        break
      }

      // Heuristic: detect template-access failures on the very first turn so
      // we don't waste tokens replaying every subsequent turn against a
      // misconfigured context.
      if (
        i === 0 &&
        chatType === 'template-editing' &&
        this.looksLikeNoTemplateAccess(turnResult.text)
      ) {
        this.updateTurn(i, {
          status: 'error',
          error:
            'AI reports it cannot see the template. Check that file_id is present in the original session\'s history and that the S3 object still exists.',
          newAssistant: turnResult.text,
        })
        this.markRemainingCancelled(i + 1)
        break
      }

      this.updateTurn(i, { status: 'done', newAssistant: turnResult.text })
      newHistory.push({ role: 'user', content: userTurns[i] })
      newHistory.push({ role: 'assistant', content: turnResult.text })
    }

    this.setState({ isReplaying: false })
  }

  private openHtmlInNewTab = (html?: string) => {
    if (!html) return
    const blob = new Blob([html], { type: 'text/html' })
    const url = URL.createObjectURL(blob)
    window.open(url, '_blank', 'noopener,noreferrer')
    setTimeout(() => URL.revokeObjectURL(url), 60000)
  }

  private looksLikeNoTemplateAccess = (text: string): boolean => {
    if (!text) return false
    const lower = text.toLowerCase()
    return (
      lower.includes('no template loaded') ||
      lower.includes("don't have a template") ||
      lower.includes('do not have a template') ||
      lower.includes("don't have access to the template") ||
      lower.includes('share your template with me') ||
      lower.includes('upload an existing template')
    )
  }

  private markRemainingCancelled = (fromIndex: number) => {
    this.setState((prev) => ({
      turns: prev.turns.map((t) =>
        t.index >= fromIndex && t.status === 'pending'
          ? { ...t, status: 'cancelled' }
          : t,
      ),
    }))
  }

  private updateTurn = (index: number, partial: Partial<ReplayTurn>) => {
    this.setState((prev) => ({
      turns: prev.turns.map((t) => (t.index === index ? { ...t, ...partial } : t)),
    }))
  }
}

export const ChatReplay = GenericRedux.createConnect(
  ChatReplayComponent,
  Constants.REDUX_STORE_HOME,
)

injectReducer(
  Constants.REDUX_STORE_HOME,
  Redux.combineReducers({
    data: GenericRedux.createGenericReducer(Constants.REDUX_STORE_HOME, {}),
  }),
)
